import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import ArtListing from '../components/ArtListing';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;

  @media (max-width: 768px) {
    padding: 0.25rem 1rem;
  }
`;

const BannerImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: contain;
  margin: 2rem 0;
  border-radius: 8px;

  @media (max-width: 768px) {
    height: 200px;
    object-fit: cover;
    margin: 0;
  }
`;

const ExhibitionHeader = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-top: 1rem;
  }
`;

const ExhibitionName = styled.h1`
  margin: 0 0 1rem 0;
  font-size: 2.5rem;
`;

const ExhibitionDescription = styled.p`
  margin: 0 0 1rem 0;
  color: #666;
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 800px;
  white-space: pre-wrap;
`;

const AddressLink = styled.a`
  color: #2196f3;
  text-decoration: none;
  font-size: 1.1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h2`
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const ArtGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #666;
`;

const ExhibitionScreen = () => {
  const { exhibitionId } = useParams();
  const navigate = useNavigate();
  const [exhibition, setExhibition] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExhibitionAndWorks = async () => {
      if (!exhibitionId) {
        setError('Invalid exhibition data');
        setLoading(false);
        return;
      }

      try {
        // Fetch exhibition data
        const exhibitionRef = doc(db, 'exhibitions', exhibitionId);
        const exhibitionDoc = await getDoc(exhibitionRef);
        
        if (!exhibitionDoc.exists()) {
          throw new Error('Exhibition not found');
        }
        
        const exhibitionData = { id: exhibitionDoc.id, ...exhibitionDoc.data() };
        setExhibition(exhibitionData);
        
        // Update document title
        document.title = `${exhibitionData.name} | ArtClvb`;

        // Fetch artworks
        const artworksList = await Promise.all(
          exhibitionData.artworks.map(async (artworkRef) => {
            const artworkDoc = await getDoc(artworkRef);
            if (artworkDoc.exists()) {
              return {
                id: artworkDoc.id,
                ...artworkDoc.data()
              };
            }
            return null;
          })
        );

        // Filter out any null values from artworks that might not exist
        const validArtworks = artworksList.filter(artwork => artwork !== null);
        setArtworks(validArtworks);
      } catch (error) {
        console.error("Error fetching exhibition and works:", error);
        setError('Failed to fetch exhibition data');
      } finally {
        setLoading(false);
      }
    };

    fetchExhibitionAndWorks();

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'ArtClvb';
    };
  }, [exhibitionId]);

  const handleArtworkClick = (artwork) => {
    navigate(`/artwork/${artwork.id}`);
  };

  const handleAddressClick = (e) => {
    e.preventDefault();
    if (exhibition?.address) {
      const encodedAddress = encodeURIComponent(exhibition.address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  if (loading) {
    return <LoadingIndicator>Loading exhibition details...</LoadingIndicator>;
  }

  if (error) {
    return <EmptyMessage>{error}</EmptyMessage>;
  }

  return (
    <Container>
      {exhibition.banner && (
        <BannerImage src={exhibition.banner} alt={exhibition.name} />
      )}
      
      <ExhibitionHeader>
        <ExhibitionName>{exhibition.name}</ExhibitionName>
        
        <ExhibitionDescription>
          {exhibition.description}
        </ExhibitionDescription>
        
        {exhibition.address && (
          <AddressLink href="#" onClick={handleAddressClick}>
            {exhibition.address}
          </AddressLink>
        )}
      </ExhibitionHeader>
      
      <Title>Artworks in this Exhibition</Title>
      
      <ArtGrid>
        {artworks.map((artwork) => (
          <ArtListing
            key={artwork.id}
            artwork={artwork}
            onClick={() => handleArtworkClick(artwork)}
          />
        ))}
      </ArtGrid>
    </Container>
  );
};

export default ExhibitionScreen; 